type LeadershipListener = (isLeader: boolean) => void;

const LOCK_NAME = "cdm-game-tab-leader";

export interface TabLeadership {
  isLeader(): boolean;
  onChange(cb: LeadershipListener): void;
  claim(): void;
  destroy(): void;
}

export function createTabLeadership(): TabLeadership {
  const listeners = new Set<LeadershipListener>();
  let leader = false;
  let held = false;
  let stepDown: (() => void) | null = null;
  let promotionAbort: AbortController | null = null;
  let destroyed = false;

  const setLeader = (value: boolean) => {
    if (leader === value) return;
    leader = value;
    for (const cb of listeners) cb(leader);
  };

  const grantHandler = () =>
    new Promise<void>((release) => {
      if (destroyed) {
        release();
        return;
      }
      stepDown = release;
      held = true;
      setLeader(true);
    });

  const onReject = () => {
    if (!held) return;
    held = false;
    stepDown = null;
    setLeader(false);
    queueForPromotion();
  };

  function queueForPromotion() {
    if (destroyed) return;
    if (typeof navigator === "undefined" || !navigator.locks) {
      setLeader(true);
      return;
    }
    promotionAbort = new AbortController();
    navigator.locks
      .request(LOCK_NAME, { mode: "exclusive", signal: promotionAbort.signal }, grantHandler)
      .catch(onReject);
  }

  function steal() {
    if (destroyed || leader) return;
    if (typeof navigator === "undefined" || !navigator.locks) {
      setLeader(true);
      return;
    }
    promotionAbort?.abort();
    promotionAbort = null;
    navigator.locks
      .request(LOCK_NAME, { mode: "exclusive", steal: true }, grantHandler)
      .catch(onReject);
  }

  const releaseLock = () => {
    const release = stepDown;
    stepDown = null;
    release?.();
  };

  window.addEventListener("pagehide", releaseLock);

  steal();

  return {
    isLeader: () => leader,
    onChange: (cb) => {
      listeners.add(cb);
      cb(leader);
    },
    claim: () => steal(),
    destroy: () => {
      destroyed = true;
      window.removeEventListener("pagehide", releaseLock);
      promotionAbort?.abort();
      releaseLock();
      listeners.clear();
    },
  };
}
